import jsPDF from 'jspdf';
import {
  formatDate,
  formatCalendarDate,
  formatCurrency,
  getServiceTypeLabel,
  buildHouseMoveSummary,
  formatItemSelectionLabel,
  enrichWorkShifts,
  getShiftPeriodLabel,
  normalizeGuryahaItemName,
  mergeGuryahaQuotationItems,
  mergeXafiisyadaQuotationItems,
  computeProjectCostTotals,
  getProjectCostPdfColumns
} from '../utils/helpers';

const pdfAmount = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const COMPANY = {
  name: 'SAHALGUUR SERVICES',
  legalName: 'SAHALGUUR SERVICES COMPANY (SSC)',
  location: 'Buulo Xuubey, H.Wadnaha',
  city: 'Mogadishu, Somalia',
  phone: '+252 61 426 2600',
  email: 'info@sahalguur.com'
};

const PAYMENT_LOGOS = {
  'EVC Plus': '/evc.png',
  'Dahabshiil Bank': '/dahabshiil.png',
  'Salaam Bank': '/salam.png',
  'Primmer Bank': '/primer.png',
  'IBS Bank': '/ibs.png',
};

const PAGE_W = 210;
const PAGE_H = 297;
const primaryR = 198, primaryG = 40, primaryB = 40;
const secondaryR = 13, secondaryG = 71, secondaryB = 161;

/** Quotation: blue text for macmiilka + shift only. Header/footer stay red. */
const pdfCustomerShiftColors = (docType) => {
  const quote = docType === 'quotation';
  return {
    labelR: quote ? secondaryR : primaryR,
    labelG: quote ? secondaryG : primaryG,
    labelB: quote ? secondaryB : primaryB,
    cardHeadR: quote ? secondaryR : primaryR,
    cardHeadG: quote ? secondaryG : primaryG,
    cardHeadB: quote ? secondaryB : primaryB
  };
};

const truncate = (text, maxLength) => {
  if (!text) return '';
  const str = String(text);
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength - 2) + '..';
};

const normalizePdfItems = (record) => {
  const raw = Array.isArray(record.items) ? record.items : [];
  const serviceType = record.serviceType || 'gurista-guryaha';

  if (serviceType === 'gurista-ganacsiyada') {
    return raw.map((item) => ({
      col1: item.description || item.itemName || '-',
      col2: '-',
      col3: item.quantity != null && item.quantity !== '' ? String(item.quantity) : '0',
      col4: item.notes || '-'
    }));
  }

  if (serviceType === 'gurista-xafiisyada') {
    return mergeXafiisyadaQuotationItems(raw).map((item) => ({
      col1: item.itemName || '-',
      col2: item.damaged ? 'Haa' : 'Maya',
      col3: item.quantity != null && item.quantity !== '' ? String(item.quantity) : '0',
      col4: null
    }));
  }

  return mergeGuryahaQuotationItems(raw).map((item) => ({
      col1: normalizeGuryahaItemName(item.itemName) || '-',
      col2: formatItemSelectionLabel(item.selection),
      col3: item.quantity != null && item.quantity !== '' ? String(item.quantity) : '0',
      col4: item.damaged ? `Dhaawac${item.notes ? ` · ${item.notes}` : ''}` : (item.notes || '-')
    }));
};

const getTableHeaders = (serviceType) => {
  if (serviceType === 'gurista-xafiisyada') {
    return { col1: 'ALAABTA', col2: 'DHAAWAC', col3: 'QTY', col4: null };
  }
  if (serviceType === 'gurista-ganacsiyada') {
    return { col1: 'SHARAXAAD', col2: null, col3: 'QTY', col4: 'FAALLO' };
  }
  return { col1: 'ALAABTA', col2: 'NOOCA', col3: 'QTY', col4: 'FAALLO' };
};

const isXamaaliRole = (department) => {
  const value = String(department || '').toLowerCase().replace(/\s+/g, '');
  return value === 'xamaali' || value === 'xamali';
};

const sameRole = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

const namesForRole = (workers, role) =>
  (workers || [])
    .filter((w) => w.employeeName && (role === 'Xamaali' ? isXamaaliRole(w.department) : sameRole(w.department, role)))
    .map((w) => w.employeeName);

const describeShift = (s) => {
  const workers = s.workers || [];
  const roleTitles = ['Packing', 'Farsamo Yaqaan'];
  const seen = new Set(roleTitles.map((title) => title.toLowerCase()));
  workers.forEach((w) => {
    const role = String(w.department || '').trim();
    if (!role || isXamaaliRole(role)) return;
    const key = role.toLowerCase().replace(/\s+/g, '');
    if (key === 'supervisor' || key === 'baabuur') return;
    if (!seen.has(role.toLowerCase())) {
      roleTitles.push(role);
      seen.add(role.toLowerCase());
    }
  });
  const cartons = s.cartonCount ?? s.packageCount;
  return {
    date: formatCalendarDate(s.shiftDate) || '—',
    type: getShiftPeriodLabel(s.shiftPeriod || 'day') || '—',
    start: s.startTime || '—',
    end: s.endTime || '—',
    supervisorName: s.supervisorName || '—',
    supervisorPhone: String(s.supervisorPhone || '').trim() || '—',
    supervisorCount: Number(s.supervisorCount) > 1 ? String(s.supervisorCount) : '',
    xamaali: namesForRole(workers, 'Xamaali'),
    baabuurPeople: namesForRole(workers, 'Baabuur'),
    roleBoxes: roleTitles.map((title) => ({ title, names: namesForRole(workers, title) })),
    cartons: cartons !== '' && cartons != null && cartons !== undefined ? String(cartons) : '—',
    vehicle: String(s.vehicleName || '').trim() || '—',
    targo: String(s.targoNumber || '').trim() || '—'
  };
};

const CARD_LINE = 4.2;
const CARD_HEAD = 5.4;

const cardBodyHeight = (rows) => 2 + Math.max(rows, 1) * CARD_LINE;

/** Same steps for measuring and drawing so the box always fits the text. */
const walkShiftSection = (doc, y, record, draw) => {
  const shifts = (record.workShifts || []).map(describeShift);
  const notes = String(record.shiftNotes || '').trim();
  if (!shifts.length && !notes) return y;
  const info = pdfCustomerShiftColors(record.__pdfDocType || 'invoice');

  const paint = (fn) => {
    if (draw) fn();
  };
  const left = 14;
  const width = PAGE_W - 28;
  const gap = 2;
  const col3 = (width - gap * 2) / 3;
  const col2 = (width - gap) / 2;

  const drawCard = (x, top, w, h, title) => {
    paint(() => {
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(226, 226, 226);
      doc.rect(x, top, w, h, 'FD');
      if (record.__pdfDocType === 'quotation') {
        doc.setFillColor(info.cardHeadR, info.cardHeadG, info.cardHeadB);
        doc.rect(x, top, w, 1, 'F');
        doc.setTextColor(info.cardHeadR, info.cardHeadG, info.cardHeadB);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.text(title, x + 2.2, top + 4.2);
      } else {
        doc.setFillColor(info.cardHeadR, info.cardHeadG, info.cardHeadB);
        doc.rect(x, top, w, CARD_HEAD, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.text(title, x + 2.2, top + 3.7);
      }
    });
  };

  const drawPair = (x, top, w, label, value) => {
    paint(() => {
      doc.setFontSize(6);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(label, x + 2.2, top);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(0, 0, 0);
      doc.text(truncate(value || '—', 18), x + w - 2.2, top, { align: 'right' });
    });
  };

  let sy = y + 2;
  shifts.forEach((s, idx) => {
    if (shifts.length > 1) {
      paint(() => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(90, 90, 90);
        doc.text(`SHIFT ${idx + 1}`, left, sy + 3);
      });
      sy += 5;
    }

    const supRows = 2 + (s.supervisorCount ? 1 : 0);
    const xamaaliRows = Math.max(s.xamaali.length, 1);
    const topH = CARD_HEAD + Math.max(cardBodyHeight(4), cardBodyHeight(supRows), cardBodyHeight(xamaaliRows));

    drawCard(left, sy, col3, topH, 'SHIFT');
    let rowY = sy + CARD_HEAD + 3.6;
    [
      ['Taariikh', s.date],
      ['Nooca', s.type],
      ['Bilow', s.start],
      ['Dhammaad', s.end]
    ].forEach(([label, value]) => {
      drawPair(left, rowY, col3, label, value);
      rowY += CARD_LINE;
    });

    const supX = left + col3 + gap;
    drawCard(supX, sy, col3, topH, 'SUPERVISOR');
    rowY = sy + CARD_HEAD + 3.6;
    [
      ['Magaca', s.supervisorName],
      ['Telefoon', s.supervisorPhone],
      ...(s.supervisorCount ? [['Tirada', s.supervisorCount]] : [])
    ].forEach(([label, value]) => {
      drawPair(supX, rowY, col3, label, value);
      rowY += CARD_LINE;
    });

    const xamX = left + (col3 + gap) * 2;
    drawCard(xamX, sy, col3, topH, 'XAMAALI');
    rowY = sy + CARD_HEAD + 3.6;
    const names = s.xamaali.length ? s.xamaali : ['—'];
    names.forEach((name) => {
      paint(() => {
        doc.setDrawColor(226, 226, 226);
        doc.setFillColor(248, 250, 252);
        doc.rect(xamX + 2, rowY - 3, col3 - 4, 3.6, 'FD');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(0, 0, 0);
        doc.text(truncate(name, 24), xamX + 3.2, rowY - 0.4);
      });
      rowY += CARD_LINE;
    });

    sy += topH + gap;

    const lowerCards = [
      { title: 'PACKAGING', pairs: [['Tirada cartons', s.cartons]], names: [] },
      {
        title: 'BAABUUR',
        pairs: [
          ['Nooca', s.vehicle],
          ['Targo', s.targo]
        ],
        names: s.baabuurPeople
      },
      ...s.roleBoxes.map((box) => ({
        title: String(box.title || '').toUpperCase(),
        pairs: [],
        names: box.names
      }))
    ];

    for (let i = 0; i < lowerCards.length; i += 2) {
      const row = lowerCards.slice(i, i + 2);
      const rowTop = sy;
      const rowH = Math.max(
        ...row.map((card) => CARD_HEAD + cardBodyHeight(card.pairs.length + Math.max(card.names.length, card.pairs.length ? 0 : 1)))
      );
      row.forEach((card, col) => {
        const x = left + col * (col2 + gap);
        drawCard(x, rowTop, col2, rowH, card.title);
        let lineY = rowTop + CARD_HEAD + 3.6;
        card.pairs.forEach(([label, value]) => {
          drawPair(x, lineY, col2, label, value);
          lineY += CARD_LINE;
        });
        const nameRows = card.names.length ? card.names : card.pairs.length ? [] : ['—'];
        nameRows.forEach((name) => {
          const textY = lineY;
          paint(() => {
            doc.setDrawColor(226, 226, 226);
            doc.setFillColor(248, 250, 252);
            doc.rect(x + 2, textY - 3, col2 - 4, 3.6, 'FD');
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7);
            doc.setTextColor(0, 0, 0);
            doc.text(truncate(name, 28), x + 3.2, textY - 0.4);
          });
          lineY += CARD_LINE;
        });
      });
      sy += rowH + gap;
    }
    sy += 1.5;
  });

  if (notes) {
    paint(() => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      if (record.__pdfDocType === 'quotation') {
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.text('Faallo:', left, sy + 3);
        doc.text(truncate(notes, 100), left + 14, sy + 3);
      } else {
        doc.setTextColor(0, 0, 0);
        doc.text(truncate(`Faallo: ${notes}`, 110), left, sy + 3);
      }
    });
    sy += CARD_LINE;
  }

  return sy + 1;
};

const measureShiftSection = (record) => {
  const shifts = record.workShifts || [];
  const notes = String(record.shiftNotes || '').trim();
  if (!shifts.length && !notes) return { height: 0 };
  const end = walkShiftSection(null, 0, record, false);
  return { height: end };
};

const drawShiftSection = (doc, y, record) => {
  const layout = measureShiftSection(record);
  if (!layout.height) return y;
  doc.setFillColor(240, 248, 255);
  doc.rect(12, y, PAGE_W - 24, layout.height, 'F');
  doc.setDrawColor(secondaryR, secondaryG, secondaryB);
  doc.rect(12, y, PAGE_W - 24, layout.height, 'S');
  walkShiftSection(doc, y, record, true);
  return y + layout.height + 2;
};

const LOGO_SRC = '/logo.png';
const HEADER_H = 30;
const FOOTER_H = 18;
const CONTENT_TOP = 34;

const loadImage = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

let logoDataUrlPromise = null;
const getCompanyLogo = () => {
  if (!logoDataUrlPromise) logoDataUrlPromise = loadImage(LOGO_SRC);
  return logoDataUrlPromise;
};

/** Red header bar + logo + SAHALGUUR SERVICES (same as invoice/quotation). */
const drawBrandedPdfHeader = async (doc, { rightLine1, rightLine2, rightLine3, companyTitle, companyTitleSize = 14 } = {}) => {
  const pageW = doc.internal.pageSize.getWidth();
  doc.setFillColor(primaryR, primaryG, primaryB);
  doc.rect(0, 0, pageW, HEADER_H, 'F');
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(12, 3, 24, 24, 2, 2, 'F');
  try {
    const logoImg = await getCompanyLogo();
    if (logoImg) doc.addImage(logoImg, 'PNG', 13, 4, 22, 22);
  } catch (e) {}

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  const logoName = companyTitle || COMPANY.name;
  let logoNameSize = companyTitleSize;
  const logoNameMaxW = pageW - 40 - 58;
  doc.setFontSize(logoNameSize);
  while (logoNameSize > 8 && doc.getTextWidth(logoName) > logoNameMaxW) {
    logoNameSize -= 0.5;
    doc.setFontSize(logoNameSize);
  }
  doc.text(logoName, 40, 12);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text(COMPANY.location, 40, 17);
  doc.text(`${COMPANY.city} · ${COMPANY.phone}`, 40, 22);

  if (rightLine1) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(rightLine1, pageW - 12, 11, { align: 'right' });
  }
  if (rightLine2) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(rightLine2, pageW - 12, 17, { align: 'right' });
  }
  if (rightLine3) {
    doc.text(rightLine3, pageW - 12, 23, { align: 'right' });
  }

  doc.setTextColor(0, 0, 0);
  return CONTENT_TOP;
};

const drawBrandedPdfFooter = async (doc) => {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const footerY = pageH - FOOTER_H;
  doc.setFillColor(secondaryR, secondaryG, secondaryB);
  doc.rect(0, footerY, pageW, FOOTER_H, 'F');

  let textX = 12;
  try {
    const logoImg = await getCompanyLogo();
    if (logoImg) {
      doc.addImage(logoImg, 'PNG', 8, footerY + 4, 10, 10);
      textX = 22;
    }
  } catch (e) {}

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(COMPANY.legalName, textX, footerY + 8);
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.text('Mahadsanid - Thank You For Your Business', textX, footerY + 12);
  doc.text(`${COMPANY.location}, ${COMPANY.city}`, textX, footerY + 16);
  doc.setFontSize(7);
  doc.text(`${COMPANY.email} | ${COMPANY.phone}`, pageW - 12, footerY + 11, { align: 'right' });
};

/**
 * Branded SAHALGUUR A4 layout.
 * Quotations paginate so every item/quantity is shown (extra A4 pages if needed).
 * Invoices stay on one A4 page.
 */
const renderBrandedA4Pdf = async (record, options) => {
  const {
    docType = 'invoice',
    refText,
    showPayment = true,
    showTotal = true,
    showAdminSignature = true,
    saveFilename
  } = options;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [PAGE_W, PAGE_H] });
  const serviceType = record.serviceType || 'gurista-guryaha';
  const allItems = normalizePdfItems(record);
  const headers = getTableHeaders(serviceType);
  const houseSummary = buildHouseMoveSummary(record);
  const info = pdfCustomerShiftColors(docType);
  const recordForShifts = {
    ...record,
    __pdfDocType: docType,
    workShifts: enrichWorkShifts(record.workShifts || [], record.pdfEmployees || [])
  };
  const shiftLayout = measureShiftSection(recordForShifts);

  const BOTTOM_BLOCK = showPayment ? 62 : 38;
  const allowMultiPage = docType === 'quotation';
  const shiftBlockH = shiftLayout.height;
  const fixedTop = 30 + 32 + (houseSummary ? 16 : 0) + shiftBlockH + 11;
  const rowH = allowMultiPage ? 5.2 : allItems.length > 22 ? 5.5 : 6.5;
  const maxRows = Math.max(3, Math.floor((PAGE_H - fixedTop - BOTTOM_BLOCK - FOOTER_H - 6) / rowH));
  const rowsToRender = allowMultiPage ? allItems : allItems.slice(0, maxRows);
  const hiddenCount = allowMultiPage ? 0 : allItems.length - rowsToRender.length;

  const typeLabel = docType === 'quotation' ? 'QUOTATION' : 'INVOICE';
  const headerOpts = {
    rightLine1: refText,
    rightLine2: typeLabel,
    rightLine3: formatDate(record.createdAt),
    companyTitle: docType === 'quotation' ? COMPANY.legalName : COMPANY.name,
    companyTitleSize: 14
  };
  let y = await drawBrandedPdfHeader(doc, headerOpts);

  // —— Customer box ——
  doc.setFillColor(250, 250, 250);
  doc.rect(12, y, PAGE_W - 24, 30, 'F');
  doc.setDrawColor(230, 230, 230);
  doc.rect(12, y, PAGE_W - 24, 30, 'S');

  doc.setTextColor(info.labelR, info.labelG, info.labelB);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text('MACMIILKA', 16, y + 6);
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(truncate(record.customerName || '-', 35), 16, y + 13);
  doc.setFontSize(9);
  doc.text(record.phone || '-', 16, y + 19);

  const rightX = 108;
  doc.setTextColor(info.labelR, info.labelG, info.labelB);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text('GUURITAANKA', rightX, y + 6);

  const drawMoveLine = (lineY, label, value) => {
    doc.setFontSize(8);
    if (docType === 'quotation') {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(info.labelR, info.labelG, info.labelB);
      doc.text(label, rightX, lineY);
      const labelW = doc.getTextWidth(`${label} `);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(truncate(value || '-', 28), rightX + labelW, lineY);
    } else {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(`${label}${truncate(value || '-', 28)}`, rightX, lineY);
    }
  };
  drawMoveLine(y + 13, 'From: ', record.fromLocation);
  drawMoveLine(y + 18, 'To: ', record.toLocation);
  drawMoveLine(y + 23, 'Date: ', formatDate(record.movingDate));

  y += 32;

  if (houseSummary) {
    doc.setFillColor(255, 248, 245);
    doc.rect(12, y, PAGE_W - 24, 14, 'F');
    doc.setDrawColor(primaryR, primaryG, primaryB);
    doc.rect(12, y, PAGE_W - 24, 14, 'S');
    doc.setFontSize(7);
    doc.setTextColor(primaryR, primaryG, primaryB);
    doc.setFont('helvetica', 'bold');
    doc.text('NOOCA GURIGA:', 16, y + 5);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.text(truncate(houseSummary, 95), 48, y + 5);
    y += 16;
  }

  if (shiftLayout.height) {
    y = drawShiftSection(doc, y, recordForShifts);
  }

  const itemsBarR = docType === 'quotation' ? primaryR : secondaryR;
  const itemsBarG = docType === 'quotation' ? primaryG : secondaryG;
  const itemsBarB = docType === 'quotation' ? primaryB : secondaryB;
  const col1 = 16;
  const col2 = 92;
  const col3 = headers.col4 ? 132 : 158;
  const col4 = 154;
  const split2 = 88;
  const split3 = headers.col4 ? 128 : 152;
  const split4 = 150;
  const pageTop = 12;
  const pageBottom = PAGE_H - FOOTER_H - 4;
  let tableTop = y;

  const paintServiceBanner = () => {
    doc.setFillColor(itemsBarR, itemsBarG, itemsBarB);
    doc.rect(12, y, PAGE_W - 24, 9, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(getServiceTypeLabel(serviceType).toUpperCase(), PAGE_W / 2, y + 6.5, { align: 'center' });
    y += 11;
  };

  const paintTableHeader = () => {
    tableTop = y;
    doc.setFillColor(itemsBarR, itemsBarG, itemsBarB);
    doc.rect(12, y, PAGE_W - 24, 7, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.text(headers.col1, col1, y + 5);
    if (headers.col2) doc.text(headers.col2, col2, y + 5);
    doc.text(headers.col3, col3, y + 5);
    if (headers.col4) doc.text(headers.col4, col4, y + 5);
    y += 7;
  };

  const closeTableBorder = () => {
    doc.setDrawColor(itemsBarR, itemsBarG, itemsBarB);
    doc.setLineWidth(0.4);
    doc.rect(12, tableTop, PAGE_W - 24, Math.max(7, y - tableTop), 'S');
    doc.setLineWidth(0.2);
  };

  const startItemsContinuationPage = () => {
    closeTableBorder();
    doc.addPage();
    y = pageTop;
    paintTableHeader();
  };

  if (allowMultiPage && y + 9 + 7 + rowH > pageBottom) {
    doc.addPage();
    y = pageTop;
  }

  paintServiceBanner();
  paintTableHeader();

  const displayRows =
    rowsToRender.length > 0
      ? rowsToRender
      : [{ col1: 'Alaab lama dooran', col2: '-', col3: '-', col4: '-' }];

  for (let index = 0; index < displayRows.length; index += 1) {
    if (allowMultiPage && y + rowH > pageBottom) {
      startItemsContinuationPage();
    }
    const item = displayRows[index];
    if (index % 2 === 0) doc.setFillColor(255, 255, 255);
    else doc.setFillColor(248, 250, 252);
    doc.rect(12, y, PAGE_W - 24, rowH, 'F');
    doc.setDrawColor(230, 230, 230);
    doc.line(12, y + rowH, PAGE_W - 12, y + rowH);
    doc.line(split2, y, split2, y + rowH);
    doc.line(split3, y, split3, y + rowH);
    if (headers.col4) doc.line(split4, y, split4, y + rowH);
    doc.setFontSize(7);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text(truncate(item.col1, 24), col1, y + rowH - 2);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    if (headers.col2) doc.text(truncate(item.col2, 14), col2, y + rowH - 2);
    doc.text(truncate(item.col3, 8), col3, y + rowH - 2);
    if (headers.col4) doc.text(truncate(item.col4, 16), col4, y + rowH - 2);
    y += rowH;
  }

  if (hiddenCount > 0) {
    doc.setFontSize(6);
    doc.setTextColor(100, 100, 100);
    doc.text(`+ ${hiddenCount} alaab oo kale (PDF A4)`, col1, y + 3);
    y += 5;
  }

  closeTableBorder();

  const footerY = PAGE_H - FOOTER_H;
  const compactBottom = showPayment ? 42 : 28;
  let blockY = allowMultiPage ? y + 2 : footerY - BOTTOM_BLOCK;
  if (allowMultiPage && blockY + compactBottom > footerY - 2) {
    doc.addPage();
    blockY = pageTop;
  }

  if (showTotal && pdfAmount(record.totalAmount) > 0) {
    const boxW = 70;
    const boxX = PAGE_W - boxW - 12;
    const boxH = 14;
    doc.setFillColor(primaryR, primaryG, primaryB);
    doc.rect(boxX, blockY, boxW, boxH, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    doc.text('WADARTA GUUD', boxX + 3, blockY + 5);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(formatCurrency(record.totalAmount), boxX + boxW - 3, blockY + 11.5, { align: 'right' });
    blockY += boxH + 2;
  } else if (docType === 'quotation') {
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text('Qiimaha: la sugayo ansixinta maamulka', PAGE_W - 12, blockY + 3, { align: 'right' });
    blockY += 7;
  }

  if (showPayment && record.paymentType) {
    const boxW = 70;
    const boxX = PAGE_W - boxW - 12;
    const payH = 16;
    doc.setFillColor(240, 247, 255);
    doc.rect(boxX, blockY, boxW, payH, 'F');
    doc.setDrawColor(secondaryR, secondaryG, secondaryB);
    doc.rect(boxX, blockY, boxW, payH, 'S');
    try {
      const paymentLogoImg = await loadImage(PAYMENT_LOGOS[record.paymentType] || '/evc.png');
      if (paymentLogoImg) doc.addImage(paymentLogoImg, 'PNG', boxX + 3, blockY + 2, 12, 12);
    } catch (e) {}
    doc.setFontSize(6);
    doc.setTextColor(secondaryR, secondaryG, secondaryB);
    doc.setFont('helvetica', 'bold');
    doc.text('LACAG BIXINTA', boxX + 18, blockY + 5);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.text(record.paymentType, boxX + 18, blockY + 9.5);
    doc.setFontSize(7);
    doc.text(record.accountNumber || '-', boxX + 18, blockY + 13.5);
    blockY += payH + 2;
  }

  const sigH = 12;
  if (allowMultiPage && blockY + sigH + 4 > footerY - 2) {
    doc.addPage();
    blockY = pageTop;
  }
  const sigY = allowMultiPage ? blockY : footerY - 22;
  doc.setFontSize(6);
  doc.setTextColor(primaryR, primaryG, primaryB);
  doc.setFont('helvetica', 'bold');
  doc.text('SAXIIXA MACMIILKA', 12, sigY);
  doc.rect(12, sigY + 1, 44, sigH, 'S');
  if (record.customerSignature) {
    try {
      doc.addImage(record.customerSignature, 'PNG', 14, sigY + 1.5, 40, sigH - 2);
    } catch (e) {}
  }

  // Always show SAXIIXA MAAMULKA on both quotation and invoice PDFs
  doc.text('SAXIIXA MAAMULKA', PAGE_W - 56, sigY);
  doc.rect(PAGE_W - 56, sigY + 1, 44, sigH, 'S');
  if (record.adminSignature) {
    try {
      doc.addImage(record.adminSignature, 'PNG', PAGE_W - 54, sigY + 1.5, 40, sigH - 2);
    } catch (e) {}
  }

  await drawBrandedPdfFooter(doc);

  doc.save(saveFilename);
  return true;
};

export const generateInvoicePDF = async (invoice) => {
  try {
    return await renderBrandedA4Pdf(invoice, {
      docType: 'invoice',
      refText: invoice.invoiceNumber || '-',
      showPayment: true,
      showTotal: true,
      showAdminSignature: true,
      saveFilename: `${invoice.invoiceNumber || `invoice-${Date.now()}`}.pdf`
    });
  } catch (error) {
    console.error('PDF Generation Error:', error);
    throw error;
  }
};

export const generateQuotationPDF = async (quotation) => {
  try {
    const ref = quotation.quotationNumber || quotation.invoiceNumber || '-';
    return await renderBrandedA4Pdf(quotation, {
      docType: 'quotation',
      refText: ref,
      showPayment: Boolean(quotation.paymentType),
      showTotal: pdfAmount(quotation.totalAmount) > 0,
      showAdminSignature: true,
      saveFilename: `${ref !== '-' ? ref : `quotation-${quotation.id || Date.now()}`}.pdf`
    });
  } catch (error) {
    console.error('Quotation PDF Error:', error);
    throw error;
  }
};

export const generateProjectCostSheetPDF = async (sheet) => {
  const { activeLines, qarashkaTotal, netIncome } = computeProjectCostTotals(sheet);
  const { showDate, showSupervisor } = getProjectCostPdfColumns(sheet, activeLines);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [PAGE_W, PAGE_H] });
  let y = await drawBrandedPdfHeader(doc, {
    rightLine1: 'QARASH MASHRUUC',
    rightLine2: truncate(sheet.title || sheet.customerName || 'Project cost', 40),
    rightLine3: formatDate(sheet.jobDate)
  });

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(sheet.title || 'Qarash Mashruuc', 14, y);
  y += 7;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  if (sheet.customerName) {
    doc.text(`Macmiil: ${truncate(sheet.customerName, 50)}`, 14, y);
    y += 5;
  }
  if (sheet.serviceType) {
    doc.text(`Adeeg: ${getServiceTypeLabel(sheet.serviceType)}`, 14, y);
    y += 5;
  }
  doc.text(`Supervisor: ${sheet.supervisor || '-'}`, 14, y);
  y += 8;

  if (!activeLines.length) {
    doc.setFontSize(9);
    doc.text('Khado la dooran ma jiraan.', 14, y);
    y += 10;
  } else {
    const colNo = 16;
    const colBusiness = 24;
    let colPrice = 118;
    let colDate = 145;
    let colSupervisor = 168;
    if (!showDate && !showSupervisor) colPrice = 150;
    else if (showDate && !showSupervisor) {
      colPrice = 108;
      colDate = 138;
    } else if (!showDate && showSupervisor) {
      colPrice = 108;
      colSupervisor = 145;
    }

    doc.setFillColor(secondaryR, secondaryG, secondaryB);
    doc.rect(14, y, PAGE_W - 28, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    doc.text('NO', colNo, y + 5.5);
    doc.text('BUSINESS', colBusiness, y + 5.5);
    doc.text('PRICE', colPrice, y + 5.5);
    if (showDate) doc.text('DATE', colDate, y + 5.5);
    if (showSupervisor) doc.text('SUPERVISOR', colSupervisor, y + 5.5);
    y += 8;

    doc.setTextColor(0, 0, 0);
    for (let idx = 0; idx < activeLines.length; idx += 1) {
      const line = activeLines[idx];
      if (y > PAGE_H - FOOTER_H - 28) {
        await drawBrandedPdfFooter(doc);
        doc.addPage();
        y = await drawBrandedPdfHeader(doc, {
          rightLine1: 'QARASH MASHRUUC',
          rightLine2: '(sii wad)',
          rightLine3: formatDate(sheet.jobDate)
        });
      }
      doc.text(String(line.lineNo || idx + 1), colNo, y + 4);
      doc.text(truncate(line.businessName, showDate && showSupervisor ? 32 : 48), colBusiness, y + 4);
      doc.text(formatCurrency(line.amount), colPrice, y + 4);
      if (showDate) {
        doc.text(formatDate(line.date || sheet.jobDate), colDate, y + 4);
      }
      if (showSupervisor) {
        doc.text(truncate(line.supervisor || sheet.supervisor || '-', 18), colSupervisor, y + 4);
      }
      y += 6;
    }
  }

  if (y > PAGE_H - FOOTER_H - 24) {
    await drawBrandedPdfFooter(doc);
    doc.addPage();
    y = CONTENT_TOP;
  }

  y += 4;
  doc.setFillColor(primaryR, primaryG, primaryB);
  doc.rect(14, y, 60, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.text(`Qarashka ${formatCurrency(qarashkaTotal)}`, 16, y + 7);
  doc.setFillColor(255, 235, 59);
  doc.setTextColor(0, 0, 0);
  doc.rect(78, y, 60, 10, 'F');
  doc.text(`Project ${formatCurrency(sheet.projectAmount)}`, 80, y + 7);
  doc.setFillColor(76, 175, 80);
  doc.setTextColor(255, 255, 255);
  doc.rect(142, y, 54, 10, 'F');
  doc.text(`Net ${formatCurrency(netIncome)}`, 144, y + 7);

  await drawBrandedPdfFooter(doc);
  doc.save(`project-cost-${sheet.id || Date.now()}.pdf`);
  return true;
};

const pdfEnsureSpace = async (doc, y, need, headerOpts) => {
  if (y + need > PAGE_H - FOOTER_H - 10) {
    await drawBrandedPdfFooter(doc);
    doc.addPage();
    return drawBrandedPdfHeader(doc, headerOpts);
  }
  return y;
};

export const generateEmployeeProfilePDF = async (employee, history = {}) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [PAGE_W, PAGE_H] });
  const headerOpts = {
    rightLine1: 'SHAQAALE PROFILE',
    rightLine2: truncate(employee?.name || '-', 36),
    rightLine3: formatDate(new Date())
  };
  let y = await drawBrandedPdfHeader(doc, headerOpts);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(employee?.name || '-', 14, y);
  y += 7;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Telefoon: ${employee?.phone || '-'}`, 14, y);
  y += 5;
  doc.text(`Qaybta: ${employee?.department || '-'}`, 14, y);
  y += 5;
  doc.text(`Bilaabay shaqada: ${formatDate(employee?.startWorkDate)}`, 14, y);
  y += 5;
  doc.text(
    `Shaqooyin (kharash): ${employee?.jobsHandled || 0} · Kharash wadar: ${formatCurrency(employee?.revenueInvolved || 0)}`,
    14,
    y
  );
  y += 10;

  const expenses = history.expenses || [];
  const shifts = history.shifts || [];

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(`Shifts / guurimo (${shifts.length})`, 14, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);

  if (!shifts.length) {
    doc.text('Wali shift lama qorin.', 14, y);
    y += 8;
  } else {
    for (const s of shifts) {
      y = await pdfEnsureSpace(doc, y, 14, headerOpts);
      const period = getShiftPeriodLabel(s.shiftPeriod) || s.shiftPeriod || '';
      const line1 = `${s.ref} · ${s.kind} · ${s.role}${period ? ` · ${period}` : ''}`;
      doc.text(truncate(line1, 95), 14, y);
      y += 4;
      const line2 = [
        s.customer,
        s.shiftDate ? formatDate(s.shiftDate) : formatDate(s.movingDate),
        s.startTime && s.endTime ? `${s.startTime}-${s.endTime}` : ''
      ]
        .filter(Boolean)
        .join(' · ');
      if (line2) {
        doc.text(truncate(line2, 95), 14, y);
        y += 4;
      }
      const route = [s.fromLocation, s.toLocation].filter(Boolean).join(' → ');
      if (route) {
        doc.text(truncate(route, 95), 14, y);
        y += 4;
      }
      const extra = [s.vehicleName, s.targoNumber ? `Targo ${s.targoNumber}` : '', s.cartonCount ? `Cartons ${s.cartonCount}` : '']
        .filter(Boolean)
        .join(' · ');
      if (extra) {
        doc.text(truncate(extra, 95), 14, y);
        y += 4;
      }
      y += 2;
    }
  }

  y = await pdfEnsureSpace(doc, y, 12, headerOpts);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(`Kharashyada (${expenses.length})`, 14, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);

  if (!expenses.length) {
    doc.text('Kharash lama xiriirin.', 14, y);
  } else {
    for (const ex of expenses) {
      y = await pdfEnsureSpace(doc, y, 6, headerOpts);
      doc.text(
        truncate(`${formatDate(ex.date)} · ${ex.category || '-'} · ${ex.description || '-'}`, 72),
        14,
        y
      );
      doc.text(formatCurrency(ex.amount), PAGE_W - 14, y, { align: 'right' });
      y += 5;
    }
  }

  await drawBrandedPdfFooter(doc);
  doc.save(`shaqaale-${(employee?.name || 'profile').replace(/\s+/g, '-')}.pdf`);
  return true;
};

export const generateInventoryItemPDF = async (item) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [PAGE_W, PAGE_H] });
  let y = await drawBrandedPdfHeader(doc, {
    rightLine1: 'INVENTORY',
    rightLine2: truncate(item?.name || '-', 36),
    rightLine3: formatDate(new Date())
  });

  const total = item?.total ?? item?.price ?? 0;

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(item?.name || '-', 14, y);
  y += 8;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const rows = [
    ['Sharaxaad', item?.description || '—'],
    ['Tirada', String(item?.quantity ?? '—')],
    ['Wadarta ($)', formatCurrency(total)],
    ['Taariikh (record)', formatDate(item?.date)],
    ['La sameeyay', formatDate(item?.createdAt)],
    ['La cusbooneysiiyay', formatDate(item?.updatedAt)]
  ];
  rows.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(`${label}:`, 14, y);
    doc.setFont('helvetica', 'normal');
    doc.text(truncate(String(value), 80), 48, y);
    y += 6;
  });

  await drawBrandedPdfFooter(doc);
  doc.save(`inventory-${(item?.name || 'item').replace(/\s+/g, '-')}.pdf`);
  return true;
};

const formatCommissionPercent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n}%`;
};

export const generateDalalePDF = async (record) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [PAGE_W, PAGE_H] });
  let y = await drawBrandedPdfHeader(doc, {
    rightLine1: 'DALALE',
    rightLine2: truncate(record?.name || '-', 36),
    rightLine3: formatDate(new Date())
  });

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(record?.name || '-', 14, y);
  y += 8;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const rows = [
    ['Telefoon', record?.phone || '—'],
    ['Degmo', record?.degmo || '—'],
    ['Commission (%)', formatCommissionPercent(record?.commissionPercent)],
    ['La sameeyay', formatDate(record?.createdAt)],
    ['La cusbooneysiiyay', formatDate(record?.updatedAt)]
  ];
  rows.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(`${label}:`, 14, y);
    doc.setFont('helvetica', 'normal');
    doc.text(truncate(String(value), 80), 48, y);
    y += 6;
  });

  await drawBrandedPdfFooter(doc);
  doc.save(`dalale-${(record?.name || 'record').replace(/\s+/g, '-')}.pdf`);
  return true;
};

export const generateDalaleListPDF = async (items = []) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [PAGE_W, PAGE_H] });
  let y = await drawBrandedPdfHeader(doc, {
    rightLine1: 'DALALE LIST',
    rightLine2: `${items.length} dalale`,
    rightLine3: formatDate(new Date())
  });

  if (!items.length) {
    doc.setFontSize(9);
    doc.text('Dalale lama diiwaangelin.', 14, y);
    await drawBrandedPdfFooter(doc);
    doc.save(`dalale-all-${Date.now()}.pdf`);
    return true;
  }

  const colName = 14;
  const colPhone = 58;
  const colDegmo = 98;
  const colComm = 158;

  doc.setFillColor(secondaryR, secondaryG, secondaryB);
  doc.rect(12, y, PAGE_W - 24, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text('MAGACA', colName, y + 5.5);
  doc.text('TELEFOON', colPhone, y + 5.5);
  doc.text('DEGMO', colDegmo, y + 5.5);
  doc.text('COMMISSION', colComm, y + 5.5);
  y += 10;

  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
  for (const row of items) {
    if (y > PAGE_H - FOOTER_H - 12) {
      await drawBrandedPdfFooter(doc);
      doc.addPage();
      y = await drawBrandedPdfHeader(doc, {
        rightLine1: 'DALALE LIST',
        rightLine2: '(sii wad)',
        rightLine3: formatDate(new Date())
      });
      y += 4;
    }
    doc.text(truncate(row.name, 22), colName, y);
    doc.text(truncate(row.phone, 16), colPhone, y);
    doc.text(truncate(row.degmo, 28), colDegmo, y);
    doc.text(formatCommissionPercent(row.commissionPercent), colComm, y);
    y += 6;
  }

  await drawBrandedPdfFooter(doc);
  doc.save(`dalale-all-${new Date().toISOString().slice(0, 10)}.pdf`);
  return true;
};

const APARTMENT_REGISTRY_HEADERS = [
  'NO',
  'NAME APPARMENTS',
  'XAFIISKA APPARMENTS',
  'PHONE NUMBER',
  'MAGACA WARDIYE',
  'PHONE NUMBER',
  'DEGMADA',
  'LOCATION',
  'DATE'
];

const apartmentRowToCells = (row, index) => [
  String(index + 1),
  row.nameApartments || '',
  row.xafiiskaApartments || '',
  row.phoneNumber || '',
  row.wardiyeName || '',
  row.wardiyePhone || '',
  row.degmada || '',
  row.location || '',
  row.date || ''
];

/** Landscape spreadsheet — yellow header row like office register. */
export const generateApartmentRegistryPDF = async (rows = []) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 10;
  const tableW = pageW - margin * 2;
  const colWs = [12, 38, 38, 30, 36, 30, 30, 38, 25];
  const colX = [];
  let x = margin;
  colWs.forEach((w) => {
    colX.push(x);
    x += w;
  });

  let y = await drawBrandedPdfHeader(doc, {
    rightLine1: 'APARTMENT REGISTRY',
    rightLine2: `${rows.length} saf`,
    rightLine3: formatDate(new Date())
  });

  const headerH = 9;
  const rowH = 7;
  const yellowR = 253;
  const yellowG = 224;
  const yellowB = 71;

  const paintTableHeader = (topY) => {
    doc.setFillColor(yellowR, yellowG, yellowB);
    doc.rect(margin, topY, tableW, headerH, 'F');
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.2);
    doc.rect(margin, topY, tableW, headerH, 'S');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(5.5);
    doc.setFont('helvetica', 'bold');
    for (let hi = 0; hi < APARTMENT_REGISTRY_HEADERS.length; hi += 1) {
      doc.text(APARTMENT_REGISTRY_HEADERS[hi], colX[hi] + 1, topY + 5.5, { maxWidth: colWs[hi] - 2 });
    }
    return topY + headerH;
  };

  if (!rows.length) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Saf ma jiraan.', margin, y + 4);
    await drawBrandedPdfFooter(doc);
    doc.save(`apartment-registry-${Date.now()}.pdf`);
    return true;
  }

  y = paintTableHeader(y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);

  for (let i = 0; i < rows.length; i += 1) {
    if (y > pageH - FOOTER_H - rowH - 4) {
      await drawBrandedPdfFooter(doc);
      doc.addPage();
      y = await drawBrandedPdfHeader(doc, {
        rightLine1: 'APARTMENT REGISTRY',
        rightLine2: '(sii wad)',
        rightLine3: formatDate(new Date())
      });
      y = paintTableHeader(y);
    }
    const cells = apartmentRowToCells(rows[i], i);
    const rowTop = y;
    doc.setDrawColor(180, 180, 180);
    doc.rect(margin, rowTop, tableW, rowH, 'S');
    doc.setTextColor(0, 0, 0);
    for (let ci = 0; ci < cells.length; ci += 1) {
      doc.text(truncate(cells[ci], ci === 0 ? 4 : 28), colX[ci] + 1, rowTop + 4.8, {
        maxWidth: colWs[ci] - 2
      });
    }
    y += rowH;
  }

  await drawBrandedPdfFooter(doc);
  doc.save(`apartment-registry-${new Date().toISOString().slice(0, 10)}.pdf`);
  return true;
};

export { APARTMENT_REGISTRY_HEADERS, apartmentRowToCells };
